/**
 * The FinanceOverview-Component displays all members in a table and displays options for filtering and sorting the members
 */

import React, { useState, useCallback, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  Typography,
  useTheme,
  Box,
  styled,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { UnfoldMore, ExpandLess, ExpandMore, Edit, Save } from "@mui/icons-material";
import * as membersTypes from "../../types/membersTypes";
import { Link } from "react-router-dom";
import CsvLinkButton from "../../components/general/CsvLinkButton";
import useFinance from "../../hooks/finance/useFinance";
import { showErrorMessage } from "../../utils/toastUtils";
import { checkBic, checkIban, getFilteredAndSortedMembers } from "../../utils/financeUtils";

// styled form component
const StyledForm = styled("form")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
    alignItems: "flex-end",
  },
  [theme.breakpoints.down("xl")]: {
    display: "flex",
    alignItems: "center",
  },
}));

/**
 * Depicts a table with all members and a filter section to filter the members
 */
const FinanceOverview: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which proivdes the styles of the FinanceOverview
   */
  const styles = {
    amountOfEntries: {
      marginBottom: "10px",
      padding: "7px",
      textAlign: "center",
      fontWeight: "bold",
    },
    filterContainer: {
      marginBottom: "10px",
    },
    filterElement: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "155px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "120px",
      },
      [theme.breakpoints.down("lg")]: {
        margin: "7px",
        width: "120px",
      },
    },
    statusFilter: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "165px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "100px",
      },
    },
    filterBtn: {
      [theme.breakpoints.up("md")]: {
        marginTop: "12px",
        marginBottom: "7px",
        marginRight: "5px",
        marginLeft: "50px",
      },
      [theme.breakpoints.down("xl")]: {
        marginTop: "15px",
        marginBottom: "7px",
        marginRight: "5px",
        marginLeft: "25px",
      },
    },
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
    sortElement: {
      margin: "7px",
      width: "205px",
    },
    tableContainer: {
      maxHeight: (window.screen.height - 75) * 0.8,
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    tableHeadSortBtn: {
      display: "flex",
      alignItems: "center",
    },
    statusFilterMain: {
      display: "block",
      "@media screen and (max-width: 350px)": {
        display: "none",
      },
    },
    statusFilterAdditional: {
      display: "none",
      "@media screen and (max-width: 350px)": {
        display: "block",
      },
    },
    accountFilterMain: {
      "@media screen and (orientation:landscape)": {
        display: "block",
      },
      "@media screen and (orientation:portrait)": {
        display: "none",
      },
    },
    accountFilterAdditional: {
      "@media screen and (orientation:landscape)": {
        display: "none",
      },
      "@media screen and (orientation:portrait)": {
        display: "block",
      },
    },
  };

  const StyledLink = styled(Link)`
    text-decoration: none;
    color: black;
    &:hover {
      text-decoration: underline;
    }
  `;

  const { memberFinanceInformation, updateFinanceInformationForMember } = useFinance();

  const [additionalFiltersState, setAddtionalFiltersState] = useState(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [accountFilter, setAccountFilter] = useState<string>("allMembers");
  const [sortOption, setSortOption] = useState<string>("");
  const [nameSort, setNameSort] = useState<string>("");

  const [editState, setEditState] = useState<{ [key: number]: boolean }>({});
  const [editData, setEditData] = useState<{
    [key: number]: { accountHolder: string | null; iban: string | null; bic: string | null };
  }>({});
  const [openDialog, setOpenDialog] = useState(false);
  // Holds the id of the member that is currently being edited
  const [currentEditId, setCurrentEditId] = useState<number | null>(null);

  // Holds the id of a member that has erroneous data (used for styling)
  const [currentEditErrorId, setCurrentEditIdError] = useState<number | null>(null);

  const filteredAndSortedMembers = useMemo(() => {
    return getFilteredAndSortedMembers({
      memberFinanceInformation,
      statusFilter,
      searchFilter,
      accountFilter,
      sortOption,
      nameSort,
    });
  }, [memberFinanceInformation, statusFilter, searchFilter, accountFilter, sortOption, nameSort]);

  const handleEditClick = (memberId: number, memberData: membersTypes.MemberFinanceInformationDto) => {
    setEditState({ ...editState, [memberId]: true });
    setEditData({
      ...editData,
      [memberId]: { accountHolder: memberData.accountHolder, iban: memberData.iban, bic: memberData.bic },
    });
  };

  const handleInputChange = (memberId: number, field: string, value: string) => {
    setEditData({ ...editData, [memberId]: { ...editData[memberId], [field]: value } });
  };

  const handleSaveClick = (memberId: number) => {
    setCurrentEditId(memberId);
    setOpenDialog(true);
  };

  const handleSubmitFailure = (memberId: number, message: string) => {
    showErrorMessage(message);
    setOpenDialog(false);
    setCurrentEditIdError(memberId);
  };

  const handleSubmit = async () => {
    if (currentEditId === null) {
      return;
    }

    const updatedData = editData[currentEditId];

    // Check if all required fields are filled
    if (!updatedData.accountHolder || !updatedData.iban || !updatedData.bic) {
      handleSubmitFailure(currentEditId, "Alle Felder müssen ausgefüllt sein.");
      return;
    }

    // Check if the IBAN format is valid
    if (!checkIban(updatedData.iban)) {
      handleSubmitFailure(currentEditId, "Ungültiges IBAN-Format.");
      return;
    }

    // Check if the BIC format is valid
    if (!checkBic(updatedData.bic)) {
      handleSubmitFailure(currentEditId, "Ungültiges BIC-Format.");
      return;
    }

    try {
      await updateFinanceInformationForMember(currentEditId, {
        accountHolder: updatedData.accountHolder,
        iban: updatedData.iban,
        bic: updatedData.bic,
      });
    } catch (error) {
      handleSubmitFailure(currentEditId, "Fehler beim Aktualisieren der Finanzinformationen");
    }

    setEditState({ ...editState, [currentEditId]: false });
    setOpenDialog(false);
    setCurrentEditId(null);
    setCurrentEditIdError(null);
  };

  /**
   * Handles the close event of the dialog
   */
  const handleDialogClose = () => {
    if (currentEditId !== null) {
      setEditState({ ...editState, [currentEditId]: false });
    }
    setOpenDialog(false);
    setCurrentEditId(null);
    setCurrentEditIdError(null);
  };

  /**
   * Handles the change event on the search filter input
   * @param event
   */
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilter(event.target.value);
  };

  /**
   * Handles the change event on the status filter input
   * @param event
   */
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setStatusFilter(event.target.value);
  };

  /**
   * Handles the change event on the account filter input
   * @param event
   */
  const handleAccountChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAccountFilter(event.target.value);
  };

  /**
   * Handles the change event on the sort input
   * @param event
   */
  const handleSortOptionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSortOption(event.target.value);
  };

  /**
   * Handles the filter toggle
   */
  const toggleFilters: VoidFunction = () => {
    setAddtionalFiltersState(!additionalFiltersState);
  };

  /**
   * Toggles between the name sort options
   */
  const toggleNameSort: VoidFunction = () => {
    switch (nameSort) {
      case "": {
        setNameSort("up");
        break;
      }
      case "up": {
        setNameSort("down");
        break;
      }
      case "down": {
        setNameSort("");
        break;
      }
    }
  };

  /**
   * Returns the sort icon for the name column
   */
  const getNameSortIcon = () => {
    switch (nameSort) {
      case "up": {
        return <ExpandLess />;
      }
      case "down": {
        return <ExpandMore />;
      }
      default:
        return <UnfoldMore />;
    }
  };

  // The additional filters
  const additionalFilters = (
    <div>
      <TextField
        sx={styles.sortElement}
        color="primary"
        onChange={handleSortOptionChange}
        value={sortOption}
        select
        label="Sortieren nach..."
      >
        <MenuItem value={""}>-</MenuItem>
        <MenuItem value={"lastchange ASC"}>Zuletzt Aktualisiert (aufsteigend)</MenuItem>
        <MenuItem value={"lastchange DESC"}>Zuletzt Aktualisiert (absteigend)</MenuItem>
      </TextField>
    </div>
  );

  const getDataForCsvExport = useCallback(() => {
    return filteredAndSortedMembers.map((member) => ({
      ID: member.memberId,
      Name: `${member.firstName} ${member.lastName}`,
      Status: member.memberStatus,
      Inhaber: member.accountHolder,
      IBAN: member.iban,
      BIC: member.bic,
      "Letzte Änderung": member.lastchange ? new Date(member.lastchange).toLocaleDateString("de-DE") : "",
    }));
  }, [filteredAndSortedMembers]);

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item>
          <Button sx={styles.button} variant="contained" component={Link} to={`/finanzuebersicht/jahresbeitrag`}>
            Jahresbeitrag
          </Button>
        </Grid>
        <Grid item>
          <Button sx={styles.button} variant="contained" component={Link} to={`/finanzuebersicht/wwbeitrag`}>
            WW-Beitrag
          </Button>
        </Grid>
        <Grid item>
          <Button sx={styles.button} variant="contained" component={Link} to={`/finanzuebersicht/sonstigereinzug`}>
            Sonstiger Einzug
          </Button>
        </Grid>
      </Grid>
      <Paper sx={styles.filterContainer}>
        <StyledForm noValidate autoComplete="off">
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <TextField label="Name" sx={styles.filterElement} color="primary" onChange={handleSearchInput} />
            </Grid>
            <Grid item xs={6} sm={3} sx={styles.statusFilterMain}>
              <TextField
                label="Status"
                sx={styles.filterElement}
                color="primary"
                onChange={handleStatusChange}
                value={statusFilter}
                select
              >
                <MenuItem value={""}>-</MenuItem>
                <MenuItem value={"Trainee"}>Trainee</MenuItem>
                <MenuItem value={"aktives Mitglied"}>aktives Mitglied</MenuItem>
                <MenuItem value={"Senior"}>Senior</MenuItem>
                <MenuItem value={"passives Mitglied"}>passives Mitglied</MenuItem>
                <MenuItem value={"Alumnus"}>Alumnus</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} sx={styles.accountFilterMain}>
              <TextField
                label="Kontodaten"
                sx={styles.filterElement}
                color="primary"
                onChange={handleAccountChange}
                value={accountFilter}
                select
              >
                <MenuItem value={"allMembers"}>alle Mitglieder</MenuItem>
                <MenuItem value={"noAccount"}>fehlende Kontodaten</MenuItem>
                <MenuItem value={"hasAccount"}>vorhandene Kontodaten</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <IconButton aria-label="more filter options" sx={styles.filterBtn} onClick={toggleFilters} size="large">
            {additionalFiltersState ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
          </IconButton>
        </StyledForm>
        {additionalFiltersState ? additionalFilters : null}
        <Box sx={styles.amountOfEntries}>{`${filteredAndSortedMembers.length} Einträge`}</Box>
      </Paper>
      <CsvLinkButton data={getDataForCsvExport()} fileName="members_export" />
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...styles.tableHeadCell, width: "23%" }}>
                <Box sx={styles.tableHeadSortBtn} onClick={toggleNameSort}>
                  <>
                    Name
                    {getNameSortIcon()}
                  </>
                </Box>
              </TableCell>
              <TableCell sx={{ ...styles.tableHeadCell, width: "10%" }}>Status</TableCell>
              <TableCell sx={{ ...styles.tableHeadCell, width: "23%" }}>Inhaber</TableCell>
              <TableCell sx={{ ...styles.tableHeadCell, width: "24%" }}>IBAN</TableCell>
              <TableCell sx={{ ...styles.tableHeadCell, width: "15%" }}>BIC</TableCell>
              <TableCell sx={{ ...styles.tableHeadCell, width: "5%" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedMembers.map((member) => (
              <TableRow hover key={`finance-member-${member.memberId}`}>
                <TableCell component="th" scope="row">
                  <Typography color="secondary">
                    <StyledLink
                      to={`/gesamtuebersicht/${member.memberId}`}
                    >{`${member.lastName}, ${member.firstName}`}</StyledLink>
                  </Typography>
                </TableCell>
                <TableCell>{member.memberStatus}</TableCell>
                <TableCell>
                  {editState[member.memberId] ? (
                    <TextField
                      fullWidth
                      value={editData[member.memberId]?.accountHolder ?? ""}
                      onChange={(e) => handleInputChange(member.memberId, "accountHolder", e.target.value)}
                      sx={{
                        backgroundColor: currentEditErrorId === member.memberId ? theme.palette.error.light : "inherit",
                        "& .MuiInputBase-input": {
                          color: currentEditErrorId === member.memberId ? "white" : "inherit",
                        },
                      }}
                    />
                  ) : (
                    member.accountHolder
                  )}
                </TableCell>
                <TableCell>
                  {editState[member.memberId] ? (
                    <TextField
                      fullWidth
                      value={editData[member.memberId]?.iban ?? ""}
                      onChange={(e) => handleInputChange(member.memberId, "iban", e.target.value)}
                      sx={{
                        backgroundColor: currentEditErrorId === member.memberId ? theme.palette.error.light : "inherit",
                        "& .MuiInputBase-input": {
                          color: currentEditErrorId === member.memberId ? "white" : "inherit",
                        },
                      }}
                    />
                  ) : (
                    member.iban
                  )}
                </TableCell>
                <TableCell>
                  {editState[member.memberId] ? (
                    <TextField
                      fullWidth
                      value={editData[member.memberId]?.bic ?? ""}
                      onChange={(e) => handleInputChange(member.memberId, "bic", e.target.value)}
                      sx={{
                        backgroundColor: currentEditErrorId === member.memberId ? theme.palette.error.light : "inherit",
                        "& .MuiInputBase-input": {
                          color: currentEditErrorId === member.memberId ? "white" : "inherit",
                        },
                      }}
                    />
                  ) : (
                    member.bic
                  )}
                </TableCell>
                <TableCell>
                  {editState[member.memberId] ? (
                    <IconButton onClick={() => handleSaveClick(member.memberId)}>
                      <Save />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleEditClick(member.memberId, member)}>
                      <Edit />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={() => handleDialogClose()}>
        <DialogTitle>Bestätigung</DialogTitle>
        <DialogContent>
          <DialogContentText>Änderungen speichern?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose()} color="primary">
            Nein
          </Button>
          <Button onClick={() => handleSubmit()} color="primary">
            Ja
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinanceOverview;
